import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  getVoucherPackStatusDisplay,
  VoucherPack,
  VoucherPackStatus,
  VoucherPackVisibility
} from '../../../../../../../models/voucher-pack';
import { EventType, EventTypeCategory} from '../../../../../../../models/event-type';
import { TaxRate } from '../../../../../../../models/tax-rate';
import { AccountService } from '../../../../../../../services/account/account.service';
import { TaxRateService } from '../../../../../../../services/tax-rate/tax-rate.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { VoucherPackService } from '../../../../../../../services/voucher-pack/voucher-pack.service';
import { MyAccount } from '../../../../../../../models/my-account';
import { ComponentHasUnsavedChanges } from '../../../../../../../unsaved-changes-guard.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { scrollToElement } from '../../../../../../../utils';
import { EventTypeStatus } from '../../../../../../../models/event-type-enums';


@Component({
  selector: 'app-voucher-pack-detail',
  templateUrl: './voucher-pack-detail.component.html',
  styleUrls: ['./voucher-pack-detail.component.css']
})
export class VoucherPackDetailComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewVoucherPack: boolean;
  public voucherPack: VoucherPack = {
    id: null,
    url: '',
    name: '',
    status: VoucherPackStatus.draft,
    event_type_id: null,
    event_type_url: null,
    event_type_name: null,
    event_type_status: null,
    event_category_id: null,
    event_category_url: null,
    event_category_name: null,
    number_of_vouchers: 1,
    price: null,
    tax_rate_id: null,
    tax_rate_url: '',
    tax_rate_name: '',
    description: '',
    confirmation_text: '',
    payment_information_text: '',
    terms_and_conditions_checkbox_text: '',
    expiration_months: null,
    expiration_days: null,
    visibility: VoucherPackVisibility.public,
    allow_purchasing_for_unlinked_customers: true,
    allow_purchase_without_login: true,
    mailchimp_last_voucher_used_event_name: '',
    mailchimp_unused_vouchers_tag_name: '',
    mailchimp_purchased_by_customer_event_name: '',
    mailchimp_purchased_for_customer_event_name: '',
    revenue_account_codes: {},
    created_at: '',
    updated_at: '',
    last_updated_by_id: null,
    last_updated_by_first_name: null,
    last_updated_by_last_name: null,
    last_updated_by_email: null,
  };
  public isEventTypeVoucher = true;

  // If this is an existing product, store the existing status to determine
  // whether changing "Draft-edit-only" fields is allowed. We cannot simply
  // read the status of the product object because then any restricted
  // actions would take effect as soon as the status is changed locally, rather
  // than upon saving.
  public existingStatus: VoucherPackStatus;

  public taxRates: TaxRate[];
  public eventTypes: EventType[];
  public eventCategories: EventTypeCategory[];
  public myAccount: MyAccount;

  public loadingExistingVoucherPack = false;
  public loadingSubmit = false;
  public loadingTaxRates = true;
  public loadingEventTypes = true;
  public loadingEventCategories = true;

  public currentSectionId = 'setup';

  public errorMessage = '';
  public errors: any = {};
  public notFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public VoucherPackVisibility = VoucherPackVisibility;
  public EventTypeStatus = EventTypeStatus;
  public getVoucherPackStatusDisplay = getVoucherPackStatusDisplay;
  public scrollToElement = scrollToElement;

  constructor(
    public accountService: AccountService,
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private taxRateService: TaxRateService,
    private eventTypeService: EventTypeService,
    private voucherPackService: VoucherPackService
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewVoucherPack = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    if (!this.isNewVoucherPack) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.notFound = true;
        this.loadingExistingVoucherPack = false;
        return;
      }

      this.loadVoucherPack(idInt);
    }

    this.loadTaxRates();
    this.loadEventTypes();
    this.loadEventCategories();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public handleSubmit() {
    if (this.loadingSubmit) { return; }

    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<VoucherPack>;
    if (this.isNewVoucherPack) {
      savePromise = this.voucherPackService.addVoucherPack(this.voucherPack);
    } else {
      savePromise = this.voucherPackService.updateVoucherPack(this.voucherPack);
    }

    savePromise
      .then(savedVoucherPack => {
        this.voucherPack = savedVoucherPack;

        this.unsavedChanges = false;

        let successMessage: string;
        if (this.isNewVoucherPack) {
          successMessage = this.voucherPack.name + ' created successfully.';
        } else {
          successMessage = this.voucherPack.name + ' updated successfully.';
        }

        this.router.navigate(
          ['/events/setup/voucher-packs'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewVoucherPack) {
            this.errorMessage = 'You do not have permission to add voucher packs.';
          } else {
            this.errorMessage = 'You do not have permission to modify this voucher pack.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRates = response.results;

        // Set the initial tax rate to the first one in the list, if the product
        // does not already have a tax rate. If this loads before the product
        // does, that's okay because the product will be overwritten.
        if (this.voucherPack.tax_rate_id === null) {
          if (this.taxRates.length > 0) {
            this.voucherPack.tax_rate_id = this.taxRates[0].id;
          }
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  public getAvailableStatuses(): VoucherPackStatus[] {
    if (this.isExistingNonDraftProduct()) {
      return [VoucherPackStatus.active, VoucherPackStatus.retired];
    } else {
      return [VoucherPackStatus.draft, VoucherPackStatus.active, VoucherPackStatus.retired];
    }
  }

  public isExistingNonDraftProduct(): boolean {
    return !this.isNewVoucherPack && (this.existingStatus !== VoucherPackStatus.draft);
  }

  public handleIsEventTypeVoucherChange() {
    this.voucherPack.event_type_id = null;
    this.voucherPack.event_category_id = null;
  }

  public hasUnlistedEventType() {
    if (!this.voucherPack.event_type_id) { return false; }

    for (const eventType of this.eventTypes) {
      if (eventType.id === this.voucherPack.event_type_id) { return false; }
    }

    return true;
  }

  public hasUnlistedTaxRate() {
    if (!this.voucherPack.tax_rate_id) { return false; }

    for (const taxRate of this.taxRates) {
      if (taxRate.id === this.voucherPack.tax_rate_id) { return false; }
    }

    return true;
  }

  public onSectionChange(sectionId: string) {
    this.currentSectionId = sectionId;
  }

  private loadVoucherPack(id: number) {
    this.loadingExistingVoucherPack = true;

    this.voucherPackService
      .getVoucherPack(id)
      .then(voucherPack => {
        this.voucherPack = voucherPack;
        this.existingStatus = voucherPack.status;

        if (this.voucherPack.event_category_id !== null) {
          this.isEventTypeVoucher = false;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingVoucherPack = false; });
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(
        1,
        3000,
        {status: EventTypeStatus.active}
      )
      .then(response => {
        this.eventTypes = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  private loadEventCategories() {
    this.loadingEventCategories = true;

    this.eventTypeService
      .getEventTypeCategories(
        1,
        3000
      )
      .then(response => {
        this.eventCategories = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingEventCategories = false;
      });
  }

  /**
   * Whether the pack's selected event category is removed (soft-deleted), i.e.
   * absent from the active-category list. Drives the pinned
   * "&lt;name&gt; (Removed)" option so the select isn't blank (PRO-1310);
   * follows the removed tax rate convention.
   */
  public eventCategoryRemoved(eventCategoryId: number): boolean {
    if ((eventCategoryId === null) || (eventCategoryId === undefined)) {
      return false;
    }
    if (!this.eventCategories) { return true; }

    for (const eventCategory of this.eventCategories) {
      if (eventCategory.id === eventCategoryId) { return false; }
    }

    return true;
  }
}
